/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  LIST_GROUPS,
  OrderByInput,
  useCurrentWorkspace,
  useGroupListFilter,
  useGroupListOrder,
  useSetGroupListOrder,
} from '@gripp/shared-logic';
import { ColumnDef } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteDataGrid, SelectionStatus } from '../infiniteDataGrid';
import { GroupTableData, useColumns } from './useColumns';

export type SelectedGroupRow = {
  id: string;
};

export type GroupListProps = {
  maxHeight?: number;
  paddingBottom?: number;
  preserveScrollKey?: string;
  assetButton?: (data: GroupTableData) => React.ReactNode;
  peopleButton?: (data: GroupTableData) => React.ReactNode;
  routinesButton?: (data: GroupTableData) => React.ReactNode;
  groupAlertsButton?: (data: GroupTableData) => React.ReactNode;
  /** Legacy top-bar buttons (shown when exactly one row is selected). */
  editGroupMenu?: (props: {
    groupId: string;
    label: string;
  }) => React.ReactNode;
  createAlertButton?: (groupId: string) => React.ReactNode;
  /** Crop Circle side-panel wiring. */
  onRowsSelectedChange?: (rows: SelectedGroupRow[]) => void;
  selectedRowIds?: string[];
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
  onItemsCountChange?: (count: number) => void;
};

export const GroupsList = ({
  maxHeight,
  paddingBottom,
  preserveScrollKey,
  assetButton,
  peopleButton,
  routinesButton,
  groupAlertsButton,
  editGroupMenu,
  createAlertButton,
  onRowsSelectedChange,
  selectedRowIds,
  selectionStatus,
  onSelectionStatusChange,
  onItemsCountChange,
}: GroupListProps) => {
  const { t } = useTranslation();

  const AssetButton = assetButton;
  const PeopleButton = peopleButton;
  const RoutinesButton = routinesButton;
  const GroupAlertsButton = groupAlertsButton;
  const EditGroupMenu = editGroupMenu;

  const { data } = useGroupListFilter();
  const { data: orderData } = useGroupListOrder();
  const { mutateAsync: setOrder } = useSetGroupListOrder();
  const workspace = useCurrentWorkspace(true);

  const setOrderData = useCallback(
    (order: OrderByInput) => {
      setOrder(order);
    },
    [setOrder]
  );

  const [filter, setFilter] = useState<any>(undefined);
  // Legacy internal selection state — only used when the legacy
  // `editGroupMenu` / `createAlertButton` props are provided.
  const [legacyGroupsSelected, setLegacyGroupsSelected] = useState<
    string[] | undefined
  >(undefined);

  const showLegacyMenu = useMemo(
    () => legacyGroupsSelected?.length === 1,
    [legacyGroupsSelected]
  );

  const updateRowsSelected = (rows: any) => {
    if (onRowsSelectedChange) {
      onRowsSelectedChange(rows.map((row: any) => ({ id: row.id })));
    } else {
      setLegacyGroupsSelected(rows.map((row: any) => row.id));
    }
  };

  useEffect(() => {
    if (!data || workspace === undefined) return;
    const f = { ...data };
    f.workspaceId = workspace?.id;
    if (f.keyword === '') delete f.keyword;
    setFilter(f);
  }, [data, workspace]);

  const columns = useColumns(
    filter,
    AssetButton,
    PeopleButton,
    RoutinesButton,
    GroupAlertsButton
  ) as ColumnDef<any, any>[];

  const selectedIdsSet = useMemo(
    () => new Set(selectedRowIds ?? []),
    [selectedRowIds]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  const hasLegacyHeader = !!EditGroupMenu || !!createAlertButton;

  const RenderLegacyHeader = () => {
    const selectedId =
      showLegacyMenu && legacyGroupsSelected
        ? legacyGroupsSelected[0]
        : undefined;
    if (!selectedId) return null;
    return (
      <>
        {EditGroupMenu && (
          <EditGroupMenu
            groupId={selectedId}
            label={t('groups.menu.editGroup')}
          />
        )}
        {createAlertButton?.(selectedId)}
      </>
    );
  };

  return !filter ? null : (
    <InfiniteDataGrid
      modelName="group"
      query={LIST_GROUPS}
      orderBy={orderData}
      filter={filter}
      pageSize={50}
      columns={columns}
      listMaxHeight={maxHeight as number}
      paddingBottom={paddingBottom as number}
      textEmpty={t('groups.common.textEmpty')}
      dataPrefix="group.listGroups"
      enableScrollRefresh={true}
      preserveScrollKey={preserveScrollKey}
      showScrollIndicator={true}
      exportFileName="groups"
      updateRowsSelected={updateRowsSelected}
      isRowSelected={onRowsSelectedChange ? isRowSelected : undefined}
      selectionStatus={selectionStatus}
      onSelectionStatusChange={onSelectionStatusChange}
      headerContent={hasLegacyHeader ? <RenderLegacyHeader /> : undefined}
      onOrderByChange={setOrderData}
      onItemsCountChange={onItemsCountChange}
    />
  );
};
